'use strict';

/**
 * Project Funding — data/logic provider (slice 1: primary load + funding-config setup + accounting overview).
 *
 * A port of the Vue project-funding screen (web/contract_admin/src/app/funding/projectFunding) onto this pict app.
 * It does NOT touch raw Meadow entities; it rides the bespoke `Finance/*` API (served at /1.0/Finance/… through
 * the same gateway), whose shapes were confirmed against the live API:
 *
 *   GET  Finance/Bundle/Digest/Project/{id}      → { Bundle:{ Digest, FundingConfiguration, ProjectFundingSolution,
 *                                                    MasterLists, FeatureToggles }, Key }   (primary load)
 *   GET  Finance/FundingConfiguration/For/Project/{id}  → { Config:[…], LineItems, Indications }
 *   PUT  …/Category   { Key, Name, Code }                        add a funding category
 *   PUT  …/Source     { Key, Name, Hash, …, Category:{Key} }     add a funding source under a category bucket
 *   PUT  …/Funds      [ { Key, Sequence, Weight, …, FundingSource:{Key} } ]  add fund(s) under a source bucket
 *   PUT  …/Buckets    [ { BucketKey, Changes:{…} } ]             edit staged bucket fields
 *   POST …/Buckets/Delete  [ bucketKey, … ]                      remove a bucket (+ its subtree)
 *   GET  Finance/FundingSolution/For/Project/{id}  → { Solution:{ …outcomes, Violations[] }, TransactionLog }
 *   GET  Finance/Profile/Customer/{id}   → { profile:{ Funds, FundingSources, FundingCategories } }  (add-dialog masters)
 *
 * The config is a nested tree of pipe-keyed "buckets": Config[] = category buckets → FundingSourceBuckets[] →
 * FundBuckets[]. Every edit/delete addresses a bucket by its `Key`. Every edit commits immediately.
 *
 * Later slices: per-line-item funding, funding simulation, purchase-order export.
 */

const libPictProvider = require('pict-provider');
const libProjectChangeOrderGating = require('../change-orders/ProjectChangeOrderGating.js');

class ProjectFundingProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._idProject = 0;
		this._idCustomer = 0;
		this._profile = null;          // { Funds, FundingSources, FundingCategories } — lazy, for add-dialog pickers
		this._profilePromise = null;   // in-flight profile fetch, so concurrent add-dialog opens share one load
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get workspaceState()
	{
		if (!this.pict.AppData.ProjectFunding)
		{
			this.pict.AppData.ProjectFunding =
			{
				Loading: false, Message: '', HasModel: false, ProjectName: '', IDProject: 0,
				Config: [], LineItems: [], ProjectCategories: [], FeatureToggles: {}, Digest: {}, Solution: {}, CanManage: false,
			};
		}
		return this.pict.AppData.ProjectFunding;
	}

	get idProject() { return this._idProject; }
	get config() { return this.workspaceState.Config || []; }
	get profile() { return this._profile; }
	get featureToggles() { return this.workspaceState.FeatureToggles || {}; }
	canExportPurchaseOrder() { return !!this.featureToggles.EnablePurchaseOrderExport; }

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pErr, pBody) => (pErr ? reject(pErr) : resolve(pBody)))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }

	_render() { if (this.pict.views['ProjectFunding']) { this.pict.views['ProjectFunding'].render(); } }

	// --- Permissions --------------------------------------------------------------------------------

	get permissions() { return this.pict.fable.EffectivePermissionsService; }

	/** Fail-closed manage gate: the funding config wires funding sources to a project, so the representative grant is
	 *  FundingSource-Project-Associate (part of the 'associate-funding' entitlement). Super-users pass; the Finance
	 *  API is the real authority. */
	canManage()
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function') || !tmpPermissions.currentUserReady()) { return false; }
		return tmpPermissions.canHash('FundingSource-Project-Associate') || tmpPermissions.canHash('FundingSource-Project-Disassociate');
	}

	// --- Front door ---------------------------------------------------------------------------------

	/** Search the customer's projects by name (front-door picker; mirrors ImportantFiles / Sample Plan). */
	async searchProjectsAsync(pTerm)
	{
		const tmpTerm = String(pTerm == null ? '' : pTerm).replace(/~/g, '').trim();
		let tmpFilter = 'FBV~Deleted~EQ~0';
		if (tmpTerm) { tmpFilter = `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`; }
		const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/${tmpFilter}/0/100`).catch(() => []);
		return (Array.isArray(tmpRows) ? tmpRows : [])
			.map((pRow) => ({ IDProject: pRow.IDProject, Name: pRow.Value || pRow.Name || `Project ${pRow.IDProject}` }))
			.sort((pA, pB) => (pA.Name.toLowerCase() < pB.Name.toLowerCase() ? -1 : (pA.Name.toLowerCase() > pB.Name.toLowerCase() ? 1 : 0)));
	}

	resetToPicker()
	{
		const tmpState = this.workspaceState;
		tmpState.Loading = false; tmpState.Message = ''; tmpState.HasModel = false; tmpState.ProjectName = ''; tmpState.IDProject = 0;
		tmpState.Config = []; tmpState.LineItems = []; tmpState.ProjectCategories = []; tmpState.Digest = {}; tmpState.Solution = {}; tmpState.FeatureToggles = {};
		this._idProject = 0;
	}

	// --- Loading ------------------------------------------------------------------------------------

	/** Primary load: the digest bundle (config + solution + digest + toggles) + the project name. */
	async loadAsync(pIDProject)
	{
		const tmpState = this.workspaceState;
		this._idProject = pIDProject;
		// The finance profile + funding are customer-scoped; take the customer from the live session so a failed
		// Project/{id} fetch can't leave it 0 (which would fetch Finance/Profile/Customer/0 → empty add-pickers).
		this._idCustomer = (this.permissions && this.permissions._currentUserCap && this.permissions._currentUserCap.IDContext) || this._idCustomer || 0;
		tmpState.Loading = true; tmpState.Message = ''; tmpState.HasModel = false; tmpState.IDProject = pIDProject;
		this._render();
		try
		{
			const [ tmpBundleResp, tmpProject ] = await Promise.all(
				[
					this._getJSON(`Finance/Bundle/Digest/Project/${pIDProject}`),
					this._getJSON(`Project/${pIDProject}`).catch(() => null),
				]);
			const tmpBundle = (tmpBundleResp && (tmpBundleResp.Bundle || tmpBundleResp)) || {};
			this._applyBundle(tmpBundle);
			this._idCustomer = this._idCustomer || (tmpProject && (tmpProject.IDCustomer || tmpProject.CustomerID)) || 0;
			tmpState.ProjectName = (tmpProject && (tmpProject.Name || tmpProject.Description)) || `Project ${pIDProject}`;
			tmpState.CanManage = this.canManage();
			// Change-order gating: when the project's status requires a Change Order, direct line-item edits (here,
			// funding-category assignment, which writes LineItem.IDFundingCategory) are locked (see the reference's
			// ProjectChangeOrderGating). Best-effort; a failure leaves funding directly editable.
			try
			{
				const tmpGating = await libProjectChangeOrderGating.fetchProjectChangeOrderGating((pURL) => this._getJSON(pURL), pIDProject, tmpProject);
				tmpState.RequiresChangeOrder = !!tmpGating.requires;
				tmpState.ProjectStatus = tmpGating.status || '';
			}
			catch (pError) { tmpState.RequiresChangeOrder = false; }
			tmpState.HasModel = true;
			tmpState.Loading = false;
			this._render();
		}
		catch (pError)
		{
			this.pict.log.error(`ProjectFunding: failed to load project ${pIDProject}: ${pError && pError.message ? pError.message : pError}`, pError);
			tmpState.Loading = false; tmpState.HasModel = false;
			tmpState.Message = 'Could not load this project’s funding.';
			this._render();
		}
	}

	/** Fold the digest-bundle payload into state. */
	_applyBundle(pBundle)
	{
		const tmpState = this.workspaceState;
		const tmpConfig = pBundle.FundingConfiguration || {};
		this._applyConfigShape(tmpConfig);
		tmpState.Digest = pBundle.Digest || {};
		tmpState.Solution = (pBundle.ProjectFundingSolution && (pBundle.ProjectFundingSolution.Solution || pBundle.ProjectFundingSolution)) || {};
		tmpState.FeatureToggles = pBundle.FeatureToggles || {};
	}

	/** Apply a FundingConfiguration payload (Config tree + line items + the project's category list) to state. */
	_applyConfigShape(pConfig)
	{
		const tmpState = this.workspaceState;
		if (!pConfig) { return; }
		if (Array.isArray(pConfig.Config)) { tmpState.Config = pConfig.Config; }
		if (Array.isArray(pConfig.LineItems)) { tmpState.LineItems = pConfig.LineItems; }
		if (Array.isArray(pConfig.FundingCategories)) { tmpState.ProjectCategories = pConfig.FundingCategories; }
	}

	/** Fold a funding-config mutation response into state (its body IS the updated config). Returns the
	 *  RefreshFundingSolution indication so the caller knows whether to re-pull the solution. */
	_applyConfigResponse(pResponse)
	{
		this._applyConfigShape(pResponse);
		return pResponse && pResponse.Indications && pResponse.Indications.RefreshFundingSolution;
	}

	/** Authoritative config re-pull (GET) — used after mutations whose response may not carry the full shape
	 *  (line-item funding, category add/remove), so the tree + line items + category list all stay in sync. */
	async refreshConfigAsync()
	{
		try
		{
			const tmpResp = await this._getJSON(this._base());
			this._applyConfigShape(tmpResp && (tmpResp.FundingConfiguration || tmpResp));
		}
		catch (pError) { this.pict.log.warn(`ProjectFunding: config refresh failed: ${pError && pError.message ? pError.message : pError}`); }
	}

	async refreshSolutionAsync()
	{
		try
		{
			const tmpSol = await this._getJSON(`Finance/FundingSolution/For/Project/${this._idProject}`);
			this.workspaceState.Solution = (tmpSol && (tmpSol.Solution || tmpSol)) || {};
		}
		catch (pError) { this.pict.log.warn(`ProjectFunding: solution refresh failed: ${pError && pError.message ? pError.message : pError}`); }
	}

	async refreshDigestAsync()
	{
		try
		{
			const tmpDigest = await this._getJSON(`Finance/Digest/Project/${this._idProject}`);
			this.workspaceState.Digest = (tmpDigest && (tmpDigest.Digest || tmpDigest)) || this.workspaceState.Digest;
		}
		catch (pError) { this.pict.log.warn(`ProjectFunding: digest refresh failed: ${pError && pError.message ? pError.message : pError}`); }
	}

	/** After a mutation: apply the returned config, then refresh digest + (if signalled) the solution, then paint.
	 *  pQuiet (a field edit — no structural bucket change) asks the view to update the accounting + computed cells
	 *  in place instead of rebuilding the whole grid, so the user's focus + in-progress edits in other cells survive. */
	async _afterMutationAsync(pResponse, pQuiet)
	{
		const tmpRefreshSolution = this._applyConfigResponse(pResponse);
		// Structural changes (add/remove category/source/fund) can change the line-item + category lists too, and
		// the mutation response doesn't always carry them — re-pull the full config authoritatively. A field edit
		// (quiet) only touches bucket fields, so the response's Config is enough (and a re-pull would reorder).
		if (!pQuiet) { await this.refreshConfigAsync(); }
		await Promise.all([ this.refreshDigestAsync(), tmpRefreshSolution ? this.refreshSolutionAsync() : Promise.resolve() ]);
		this.workspaceState.CanManage = this.canManage();
		const tmpView = this.pict.views['ProjectFunding'];
		if (pQuiet && tmpView && (typeof tmpView.afterFieldSave === 'function')) { tmpView.afterFieldSave(); }
		else { this._render(); }
	}

	// --- Add-dialog master lists (lazy) -------------------------------------------------------------

	/** Load the customer's funds / funding sources / categories for the add pickers. Cached; the lists are large
	 *  (10k+), so this is deferred until an add-dialog opens rather than carried on the primary load. */
	loadProfileAsync()
	{
		if (this._profile) { return Promise.resolve(this._profile); }
		if (this._profilePromise) { return this._profilePromise; }   // coalesce concurrent opens onto one fetch
		const tmpCustomer = this._idCustomer || (this.permissions && this.permissions._currentUserCap && this.permissions._currentUserCap.IDContext) || 0;
		this._profilePromise = (async () =>
		{
			try
			{
				if (!tmpCustomer) { throw new Error('No customer in scope for the finance profile.'); }
				const tmpResp = await this._getJSON(`Finance/Profile/Customer/${tmpCustomer}`);
				this._profile = (tmpResp && tmpResp.profile) || { Funds: [], FundingSources: [], FundingCategories: [] };
			}
			catch (pError)
			{
				this.pict.log.error(`ProjectFunding: could not load the finance profile: ${pError && pError.message ? pError.message : pError}`, pError);
				// Leave _profile null so a later open retries; return an empty shell to the current caller.
				return { Funds: [], FundingSources: [], FundingCategories: [] };
			}
			finally { this._profilePromise = null; }
			return this._profile;
		})();
		return this._profilePromise;
	}

	// --- Config lookups -----------------------------------------------------------------------------

	/** Find any bucket (category / source / fund) in the config tree by its pipe Key. Returns { node, type, parent }. */
	findBucket(pKey)
	{
		const tmpConfig = this.config;
		for (let c = 0; c < tmpConfig.length; c++)
		{
			const tmpCat = tmpConfig[c];
			if (tmpCat.Key === pKey) { return { node: tmpCat, type: 'category', parent: null }; }
			const tmpSources = tmpCat.FundingSourceBuckets || [];
			for (let s = 0; s < tmpSources.length; s++)
			{
				const tmpSrc = tmpSources[s];
				if (tmpSrc.Key === pKey) { return { node: tmpSrc, type: 'source', parent: tmpCat }; }
				const tmpFunds = tmpSrc.FundBuckets || [];
				for (let f = 0; f < tmpFunds.length; f++)
				{
					if (tmpFunds[f].Key === pKey) { return { node: tmpFunds[f], type: 'fund', parent: tmpSrc }; }
				}
			}
		}
		return null;
	}

	// --- Mutations ----------------------------------------------------------------------------------

	_assertCanManage() { if (!this.canManage()) { throw new Error('You don’t have permission to configure funding for this project.'); } }

	_base() { return `Finance/FundingConfiguration/For/Project/${this._idProject}`; }

	/** Add a funding category to the project (pCategory = a profile FundingCategory { Key, Name, Code }). */
	async addCategoryAsync(pCategory)
	{
		this._assertCanManage();
		if (!pCategory || !pCategory.Key) { throw new Error('Pick a funding category.'); }
		const tmpResp = await this._putJSON(`${this._base()}/Category`, { Key: pCategory.Key, Name: pCategory.Name || '', Code: pCategory.Code || '' });
		await this._afterMutationAsync(tmpResp);
	}

	/** Add a funding source under a category bucket (pSource = a profile FundingSource; pCategoryBucketKey = the
	 *  category bucket's pipe Key). Seeds the default editable fields the source dialog uses. */
	async addSourceAsync(pSource, pCategoryBucketKey)
	{
		this._assertCanManage();
		if (!pSource || !pSource.Key) { throw new Error('Pick a funding source.'); }
		if (!pCategoryBucketKey) { throw new Error('Choose the category to add it under.'); }
		const tmpBody =
		{
			Key: pSource.Key, Name: pSource.Name || '', Hash: pSource.Hash || '', AccountCode: pSource.AccountCode || '',
			Priority: '1', PriorityWeight: '1', FundCap: '0', ProjectFundCap: '0', PurchaseOrderItem: '',
			Category: { Key: pCategoryBucketKey },
		};
		const tmpResp = await this._putJSON(`${this._base()}/Source`, tmpBody);
		await this._afterMutationAsync(tmpResp);
	}

	/** Add one or more funds under a source bucket (pFunds = profile Fund[]; pSourceBucketKey = source bucket key). */
	async addFundsAsync(pFunds, pSourceBucketKey)
	{
		this._assertCanManage();
		const tmpFunds = (Array.isArray(pFunds) ? pFunds : [ pFunds ]).filter((pFund) => pFund && pFund.Key);
		if (!tmpFunds.length) { throw new Error('Pick at least one fund.'); }
		if (!pSourceBucketKey) { throw new Error('Choose the funding source to add it under.'); }
		// Unlike /Source (which parses the pipe bucket key), the /Funds endpoint keys the join by the RAW
		// IDFundingSource — it does `Number(FundingSource.Key)`, so a pipe bucket key here becomes NaN and the
		// write "succeeds" while persisting NO FundingSourceFundJoin. Segment [2] of the source bucket key is the
		// IDFundingSource (finance toBucketKey: [FundingCategory, FundingCategoryProjectJoin, FundingSource, …]);
		// send that, matching what the Vue funding screen sends.
		const tmpSourceID = this._sourceIDFromBucketKey(pSourceBucketKey);
		if (!tmpSourceID) { throw new Error('Could not resolve the funding source to add the fund under.'); }
		const tmpBody = tmpFunds.map((pFund) => ({ Key: pFund.Key, Sequence: '1', Weight: '1', PurchaseOrderItem: '', FundingSource: { Key: tmpSourceID } }));
		const tmpResp = await this._putJSON(`${this._base()}/Funds`, tmpBody);
		await this._afterMutationAsync(tmpResp);
	}

	/** The raw IDFundingSource from a source bucket pipe key (`IDCat|inst|IDSrc|inst|…`), or '' when unresolvable. */
	_sourceIDFromBucketKey(pKey)
	{
		const tmpKey = String(pKey == null ? '' : pKey);
		const tmpID = (tmpKey.indexOf('|') >= 0) ? tmpKey.split('|')[2] : tmpKey;
		return (tmpID && (tmpID !== 'null') && !Number.isNaN(Number(tmpID))) ? tmpID : '';
	}

	/** Save edited fields on one bucket: PUT …/Buckets [{ BucketKey, Changes }]. */
	async saveBucketAsync(pBucketKey, pChanges)
	{
		this._assertCanManage();
		if (!pBucketKey || !pChanges || !Object.keys(pChanges).length) { return; }
		const tmpResp = await this._putJSON(`${this._base()}/Buckets`, [ { BucketKey: pBucketKey, Changes: pChanges } ]);
		await this._afterMutationAsync(tmpResp, true);   // quiet: a field edit, keep the user's focus
	}

	/** Remove buckets (a category / source / fund and its subtree): POST …/Buckets/Delete [ keys ]. */
	async deleteBucketsAsync(pKeys)
	{
		this._assertCanManage();
		const tmpKeys = (Array.isArray(pKeys) ? pKeys : [ pKeys ]).filter(Boolean);
		if (!tmpKeys.length) { return; }
		const tmpResp = await this._postJSON(`${this._base()}/Buckets/Delete`, tmpKeys);
		await this._afterMutationAsync(tmpResp);
	}

	// --- Purchase-order export (gated on FeatureToggles.EnablePurchaseOrderExport) -------------------
	// GET Finance/Export/PurchaseOrder/For/Project/{id} returns a DocumentArtifact envelope
	// { GUIDDocumentArtifact, Type, Version, Datum } where Datum = { File (plaintext), Files:[{Types,Content}],
	// FileName, Violations, DepletionHistory, TransactionLog, IDProject }. NOTE: the server ALSO persists the
	// generated artifact on every call (by design — the same as the Vue export). It fails 422 ("… missing a funding
	// category" / project has no Contract) BEFORE generating, so an incomplete project persists nothing.

	async exportPurchaseOrderAsync()
	{
		try
		{
			const tmpResp = await this._getJSON(`Finance/Export/PurchaseOrder/For/Project/${this._idProject}`);
			const tmpDatum = (tmpResp && tmpResp.Datum) || {};
			const tmpFiles = Array.isArray(tmpDatum.Files) && tmpDatum.Files.length
				? tmpDatum.Files
				: ((tmpDatum.File != null) ? [ { Types: [ 'PlainText' ], Content: String(tmpDatum.File) } ] : []);
			return {
				GUID: (tmpResp && tmpResp.GUIDDocumentArtifact) || '',
				FileName: tmpDatum.FileName || `${(tmpResp && tmpResp.GUIDDocumentArtifact) || 'PurchaseOrder'}.dat`,
				Files: tmpFiles,
				PrimaryContent: (tmpDatum.File != null) ? String(tmpDatum.File) : (tmpFiles.length ? String(tmpFiles[0].Content || '') : ''),
				Violations: Array.isArray(tmpDatum.Violations) ? tmpDatum.Violations : [],
			};
		}
		catch (pError)
		{
			throw new Error(this._humanizeError(pError, 'Could not generate the purchase order.'));
		}
	}

	/** Pull a human message out of a rest-client error whose .message is a stringified JSON error body
	 *  ({ code, message }) — the shape Headlight-Rest-Client wraps a 4xx response in. */
	_humanizeError(pError, pFallback)
	{
		const tmpRaw = (pError && pError.message) ? String(pError.message) : '';
		if (tmpRaw)
		{
			try { const tmpParsed = JSON.parse(tmpRaw); if (tmpParsed && tmpParsed.message) { return String(tmpParsed.message); } } catch (pIgnore) { /* not a JSON body */ }
			if (tmpRaw.charAt(0) !== '{') { return tmpRaw; }
		}
		return pFallback;
	}

	// --- Funding simulation (what-if; read-only, no persistence) -------------------------------------
	// POST Finance/Scenario/Simulate/For/Project/{id}/v2 with { LineItemsToFund:[{ LineItem:{Key}, Category:{Key},
	// Amount }] } computes a what-if funding solution for paying the given amounts against those line items, WITHOUT
	// touching any live data (ScenarioController is read-only — the non-v2 route is deprecated and reads a different
	// body key, LineItemsToPay). Returns { Scenario:{ LogEntry[], TransactionLog[], … }, Solution:{ FinalPaymentAmount,
	// ActualPaymentAmount, FundingTypeOutcomes[], FundOutcomes[], Violations[], … (amounts are strings) }, LookupData }.

	async simulateAsync(pLineItemsToFund)
	{
		const tmpLineItems = (Array.isArray(pLineItemsToFund) ? pLineItemsToFund : []).filter((pRow) => pRow && pRow.LineItem && pRow.LineItem.Key);
		if (!tmpLineItems.length) { throw new Error('Pick at least one line item to simulate.'); }
		try
		{
			return await this._postJSON(`Finance/Scenario/Simulate/For/Project/${this._idProject}/v2`, { LineItemsToFund: tmpLineItems });
		}
		catch (pError)
		{
			throw new Error(this._humanizeError(pError, 'Could not run the funding simulation.'));
		}
	}

	// --- Line-item funding (slice 2) ----------------------------------------------------------------

	get lineItems() { return this.workspaceState.LineItems || []; }
	get projectCategories() { return this.workspaceState.ProjectCategories || []; }

	/** The line items with their resolved funding-category name, for the grid. Uncategorized is represented by the
	 *  server as Category.Key === '0' (a sentinel), NOT an empty/absent key — treat that as uncategorized. */
	lineItemRows()
	{
		const tmpByKey = {};
		this.projectCategories.forEach((pCat) => { tmpByKey[String(pCat.Key)] = pCat.Name; });
		return this.lineItems.map((pItem) =>
		{
			const tmpRaw = (pItem.Category && (pItem.Category.Key != null)) ? String(pItem.Category.Key) : '';
			const tmpUncategorized = (!tmpRaw || (tmpRaw === '0'));
			return {
				Key: pItem.Key,
				ItemCode: pItem.Code || pItem.ItemCode || '',
				Name: pItem.Name || '',
				CategoryKey: tmpUncategorized ? '' : tmpRaw,
				CategoryName: tmpUncategorized ? '' : (tmpByKey[tmpRaw] || (pItem.Category && pItem.Category.Name) || ''),
				IsUncategorized: tmpUncategorized,
			};
		});
	}

	/** Assign a funding category to a line item. There is NO un-assign — the Finance API resolves the category by
	 *  key and rejects a '0'/empty key ("Record not found"), matching the Vue (which only ever reassigns). So a
	 *  falsy/'0' key is a no-op here. Ripples into the config + digest + solution. */
	async saveLineItemCategoryAsync(pLineItemKey, pCategoryKey)
	{
		this._assertCanManage();
		// When the project's status requires a Change Order, line-item modifications (this writes LineItem) are locked.
		if (this.workspaceState.RequiresChangeOrder) { throw new Error(`This project’s status (${this.workspaceState.ProjectStatus || ''}) requires a Change Order to modify line items.`); }
		if (!pLineItemKey || !pCategoryKey || (String(pCategoryKey) === '0')) { return; }
		await this._putJSON('Finance/LineItem', { Key: pLineItemKey, Category: { Key: pCategoryKey } });
		await this.refreshConfigAsync();
		await Promise.all([ this.refreshDigestAsync(), this.refreshSolutionAsync() ]);
		this._render();
	}
}

module.exports = ProjectFundingProvider;
module.exports.default_configuration = { ProviderIdentifier: 'ProjectFunding' };
